import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import {
  activateSimCardMsg,
  fieldCantBeEmptyMsg,
  invalidSIMCardNumberMsg,
  orderEsimMsg,
  orderNewSimCardMsg,
  simCardMsg,
  simCardOneSimPerSubscriptionMsg,
  t,
} from '../../../../common/i18n/index.js';
import { Dropdown } from '../../../../common/react-hook-form/components/Dropdown.js';
import { ACTIVATE_EXISTING, SimOption, isInvalidSimCardNumber } from '../../../../common/utils/simCardUtils.js';
import { SimType } from '../../../../generated/api/simType.js';
import { SimCardNumberInput } from '../../../SimCardNumberInput/SimCardNumberInput.js';
import type { SelectRadioItem } from '../../../../common/react-hook-form/components/SelectRadio.js';
import type { SimCardConfiguration } from '../../../../common/types/subscription.js';

import './SimCardDuringNewSubscriptionOrder.scss';
import './PlaceOrderSimCardCoCo.scss';

export interface PlaceOrderSimCardCoCoProps {
  cartItemInstanceId: string;
  onClickSimCardNumberHelp: () => void;
  simCardConfiguration: SimCardConfiguration;
  simOptions: SimOption[];
  /** SIM card numbers used across all order items, for duplicate detection */
  simCardNumbers: string[];
  /** Backend validation error from useSubscriptionConfigFormCoCo */
  backendError?: string;
  showESIM?: boolean;
  showPSIM?: boolean;
  showActiveSim?: boolean;
}

const getFallbackSimType = (showPSIM: boolean, showESIM: boolean): SimType | undefined => {
  if (showPSIM) {
    return SimType.PHYSICAL;
  }
  return showESIM ? SimType.ESIM : undefined;
};

/** The SIM choice of Fixed Mobile Broadband, presented as a dropdown. Other CoCo products use PlaceOrderSimCard. */
export const PlaceOrderSimCardCoCo = ({
  cartItemInstanceId,
  onClickSimCardNumberHelp,
  simCardConfiguration,
  simOptions,
  simCardNumbers,
  backendError,
  showESIM = true,
  showPSIM = true,
  showActiveSim = true,
}: PlaceOrderSimCardCoCoProps) => {
  const {
    control,
    watch,
    setValue,
    unregister,
    formState: { isReady },
  } = useFormContext();

  const simTypeFieldName = `configuration.${cartItemInstanceId}.simCardConfiguration.simType`;
  const simCardNumberFieldName = `configuration.${cartItemInstanceId}.simCardConfiguration.simCardNumber`;
  const selectedType = watch(simTypeFieldName);
  const defaultValue =
    simCardConfiguration.simSelection === ACTIVATE_EXISTING ? ACTIVATE_EXISTING : simCardConfiguration?.simType;

  useEffect(() => {
    if (isReady && selectedType === undefined && defaultValue !== undefined) {
      setValue(simTypeFieldName, defaultValue);
    }
  }, [isReady, simTypeFieldName, selectedType, defaultValue, setValue]);

  // The existing SIM option is only offered together with customer-owned equipment, so a selection made before the
  // equipment changed has to be reset, together with the SIM number entered for it.
  useEffect(() => {
    if (!isReady || showActiveSim || selectedType !== ACTIVATE_EXISTING) {
      return;
    }

    const fallbackSimType = getFallbackSimType(showPSIM, showESIM);
    if (fallbackSimType) {
      setValue(simTypeFieldName, fallbackSimType, { shouldDirty: true, shouldTouch: true });
    }
    unregister(simCardNumberFieldName);
  }, [
    isReady,
    selectedType,
    showActiveSim,
    showESIM,
    showPSIM,
    simTypeFieldName,
    simCardNumberFieldName,
    setValue,
    unregister,
  ]);

  // Watch the SIM number to include in the validate closure
  const simCardNumber = useWatch({ name: simCardNumberFieldName });

  const simItems: SelectRadioItem[] = [];
  if (simOptions.includes(SimOption.ORDER_NEW_PHYSICAL) && showPSIM) {
    simItems.push({
      label: t.QYXA(orderNewSimCardMsg),
      value: SimType.PHYSICAL,
    });
  }
  if (simOptions.includes(SimOption.ORDER_NEW_ESIM) && showESIM) {
    simItems.push({
      label: t.DD3Q(orderEsimMsg),
      value: SimType.ESIM,
    });
  }
  if (simOptions.includes(SimOption.ACTIVATE_EXISTING_PHYSICAL) && showActiveSim) {
    simItems.push({
      label: t.M1E7(activateSimCardMsg),
      value: ACTIVATE_EXISTING,
    });
  }

  const validateSimCard = (value: string) => {
    if (value && isInvalidSimCardNumber(value)) {
      return t.SDXB(invalidSIMCardNumberMsg);
    }

    // Backend validation error from the hook (e.g. SIM already in use)
    if (backendError && value === simCardNumber) {
      return backendError;
    }

    if (simCardNumbers.filter(otherSimCardNumber => otherSimCardNumber === value).length > 1) {
      return t.RFH9(simCardOneSimPerSubscriptionMsg);
    }

    return undefined;
  };

  const esimInfo = selectedType === SimType.ESIM && (
    <div className="of-sim-card-selection__esim-info">
      <p>
        {t.MF9P(
          'eSIM is a digital SIM card. At the moment eSIM compatible devices are the newest iPhones, iPads and Samsung Galaxy Watches.'
        )}
      </p>
      <a href="https://elisa.fi/esim/" target="_blank" rel="noopener noreferrer">
        {t.MC5L('eSIM compatible devices')}
      </a>
    </div>
  );
  const existingSimInput = selectedType === ACTIVATE_EXISTING && (
    <Controller
      name={simCardNumberFieldName}
      control={control}
      shouldUnregister={true}
      defaultValue={simCardConfiguration.simCardNumber || ''}
      rules={{
        required: { value: true, message: t.VPVR(fieldCantBeEmptyMsg) },
        validate: validateSimCard,
      }}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <SimCardNumberInput
          error={error?.message}
          onChange={onChange}
          value={value}
          onAppendedButtonClick={onClickSimCardNumberHelp}
        />
      )}
    />
  );

  return (
    <div id={`simCardConfiguration-${cartItemInstanceId}`} className={dsClass.MARGIN_TOP_2}>
      <div
        className={`${dsClass.BORDER_WIDTH_1} ${dsClass.BORDER_STYLE_SOLID} ${dsClass.PADDING_2} ${dsClass.GRID} bundle-addon-group`}
      >
        <div className={dsClass.GRID_ROW}>
          <div
            className={`${dsClass.GRID_COL_L_6} ${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_M_6} ${dsClass.GRID_COL_XS_4}`}
          >
            <Dropdown
              name={simTypeFieldName}
              label={t.PIZC(simCardMsg)}
              items={simItems}
              selectedValue={selectedType ?? defaultValue}
            />
            {esimInfo}
            {existingSimInput && <div className="of-sim-card-dropdown-input">{existingSimInput}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};
